import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  Alert,
  Skeleton
} from '@material-ui/lab';

import SpecComment from '../../../models/SpecComment';
import { CommentInput } from './CommentInput';
import { CommentItem } from './CommentItem';
import { Paging } from '../../../interfaces/pagination';
import useLoadingStatus from '../../../hooks/useLoadingStatus';

export interface SpecCommentsProps {
  specId: string
  onCommentRead?: (id: number) => void
}

const useStyle = makeStyles(() => ({
  container: {
    height: '100%',
    minHeight: '50vh',
  },
  feedsWrapper: {
    overflow: 'hidden auto',
    '&::-webkit-scrollbar': {
      width: '0.4em'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,0.2)'
    }
  }
}));

export const SpecComments = (props: SpecCommentsProps) => {
  const { specId, onCommentRead } = props;
  const [comments, setComments] = useState<SpecComment[]>([]);
  const [paging, setPaging] = useState<Paging|null>(null);

  const { isLoading, errorMessage, executeWithStatus } = useLoadingStatus();
  const feedsRef = useRef<HTMLDivElement|null>();
  const classes = useStyle();

  const hint = 'Your notes are publicly visible on all Dev League cohorts that use this assignment.';

  // scrolls feed to the end
  const scrollToEnd = () => {
    setTimeout(() => {
      const { current } = feedsRef;
      if (current) current.scrollTo(0, current.scrollHeight);
    });
  };

  // loads latest comments
  const loadComments = async () => {
    await executeWithStatus(async () => {
      setComments([]);
      const result = await SpecComment.find({
        specId,
        $limit: 10
      });
      setPaging(result.paging);
      setComments(result.items.reverse());
      scrollToEnd();
    });
  };

  // loads more comments to append
  const loadMoreComments = async () => {
    await executeWithStatus(async () => {
      const result = await SpecComment.find({
        specId,
        $limit: 10,
        $page: (paging?.currentPage || 0) + 1
      });
      const existingIds = comments.map(c => c.id);
      const newComments = result.items.filter(c => !existingIds.includes(c.id));
      setPaging(result.paging);
      setComments([...newComments.reverse(), ...comments]);
      setTimeout(() => feedsRef.current?.scrollTo(0, 1));
    });
  };

  // reload comments when spec id changes
  useEffect(() => {
    loadComments();
  }, [specId]);

  // trigger read event
  useEffect(() => {
    let timeout: NodeJS.Timeout|null = null;
    if (comments.length) {
      timeout = setTimeout(async () => {
        try {
          const _comment = comments[comments.length - 1];
          await _comment.read();
          if (onCommentRead) onCommentRead(_comment.id);
        } catch (e) {
          console.error(e);
        } finally {
          timeout = null;
        }
      }, 3000);
    }
    return () => {
      if (timeout !== null)
        clearTimeout(timeout);
    };
  }, [comments.length]);

  return (
    <Grid container direction="column" className={classes.container}>
      <Grid ref={ref => feedsRef.current = ref} item xs className={classes.feedsWrapper} onScroll={e => {
        if (isLoading) return;
        if (paging && !paging.nextPage) return;
        if (e.currentTarget.scrollTop <= 0) loadMoreComments();
      }}>
        <Box width="100%">
          {!!errorMessage && (
            <Box pb={2}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => loadMoreComments()}>
                    Try Again
                  </Button>
                }
              >
                {errorMessage}
              </Alert>
            </Box>
          )}

          {(() => {
            if (isLoading)
              return (
                <Box pb={2}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <Skeleton variant="circle" width={40} height={40} />
                    </Grid>
                    <Grid item>
                      <Skeleton variant="text" width={200} />
                      <Skeleton variant="text" width={130} height={10} />
                    </Grid>
                  </Grid>
                  <Box py={1} pl={6.2}>
                    <Skeleton variant="rect" height={75} />
                  </Box>
                </Box>
              );

            if (paging?.nextPage)
              return (
                <Box pt={1} pb={2} justifyContent="center" display="flex">
                  <Button
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => loadMoreComments()}>
                    Load More
                  </Button>
                </Box>
              );

            if (!comments.length)
              return (
                <Box p={2} style={{textAlign: 'center'}}>
                  <Typography>No notes yet, be the first to add a note!</Typography>
                  <Typography>{hint}</Typography>
                </Box>
              );
          })()}

          {comments.map((comment, index) => (
            <Box pb={2} key={comment.id} data-comment-id={comment.id}>
              <CommentItem
                comment={comment}
                divider={(index + 1) < comments.length}
                onDeleted={() => setComments(comments.filter(c => c !== comment))}
              />
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item>
        <Box pt={2}>
          {!!comments.length && <Typography style={{fontSize: '0.9em'}} color="error">{hint}</Typography>}
          <CommentInput
            specId={specId}
            onNewComment={(comment) => {
              setComments([...comments, comment]);
              scrollToEnd();
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default SpecComments;

export {
  CommentInput,
  CommentItem
};
